import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Plus, Filter, X, ClipboardList, Loader2, ChevronDown, ChevronUp, Trash2,
  LayoutGrid, List, GripVertical,
} from 'lucide-react';
import {
  DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors,
  useDroppable, useDraggable, DragOverlay,
  type DragStartEvent, type DragEndEvent,
} from '@dnd-kit/core';
import { api } from '@/utils/api';
import { formatINR, formatDate } from '@/utils/formatters';

interface PurchaseOrder {
  id: string;
  number: string;
  formalNumber?: string | null;
  status: string;
  issueDate: string;
  deliveryDate?: string;
  total: number;
  vendor?: { id: string; name: string } | null;
}

type ViewMode = 'kanban' | 'list';

const COLUMNS: { key: string; label: string; color: string }[] = [
  { key: 'draft',     label: 'Draft',     color: 'border-gray-400' },
  { key: 'issued',    label: 'Issued',    color: 'border-blue-400' },
  { key: 'confirmed', label: 'Confirmed', color: 'border-purple-400' },
  { key: 'partial',   label: 'Partial',   color: 'border-yellow-400' },
  { key: 'received',  label: 'Received',  color: 'border-emerald-400' },
  { key: 'cancelled', label: 'Cancelled', color: 'border-red-400' },
];

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-600',
  sent: 'bg-blue-50 text-blue-700',
  approved: 'bg-green-50 text-green-700',
  confirmed: 'bg-purple-50 text-purple-700',
  partially_received: 'bg-yellow-50 text-yellow-700',
  received: 'bg-emerald-50 text-emerald-700',
  cancelled: 'bg-red-50 text-red-700',
};

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  sent: 'Sent',
  approved: 'Approved',
  confirmed: 'Confirmed',
  partially_received: 'Partially Received',
  received: 'Received',
  cancelled: 'Cancelled',
};

/* ── @dnd-kit helper components ── */

function PODroppableArea({ columnKey, children }: { columnKey: string; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: columnKey });
  return (
    <div
      ref={setNodeRef}
      className={`flex-1 space-y-2 p-3 overflow-y-auto max-h-[calc(100vh-320px)] transition-colors ${isOver ? 'bg-[var(--saffron-50)] ring-2 ring-[var(--ink-3)] ring-inset rounded-b-xl' : ''}`}
    >
      {children}
    </div>
  );
}

function DraggablePOCard({
  order,
  isActive,
  onNavigate,
  onDelete,
}: {
  order: PurchaseOrder;
  isActive: boolean;
  onNavigate: () => void;
  onDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: order.id });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={onNavigate}
      className={`rounded-lg border border-gray-200 bg-white p-3 shadow-sm hover:shadow-md transition-shadow cursor-grab active:cursor-grabbing touch-none ${isActive || isDragging ? 'opacity-40' : ''}`}
    >
      <div className="mb-2 flex items-start justify-between">
        <h4 className="text-sm font-medium text-gray-900">{order.formalNumber || order.number}</h4>
        <GripVertical size={14} className="flex-shrink-0 text-gray-300" />
      </div>
      <p className="mb-2 text-xs text-gray-500">{order.vendor?.name || 'No vendor'}</p>
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-900">{formatINR(order.total)}</span>
        <span className="text-xs text-gray-400">{formatDate(order.issueDate)}</span>
      </div>
      {order.deliveryDate && (
        <p className="mt-1 text-xs text-gray-400">Delivery: {formatDate(order.deliveryDate)}</p>
      )}
      {order.status === 'draft' && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="mt-2 flex w-full items-center justify-center rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-700 transition-colors hover:bg-red-100"
        >
          <Trash2 size={12} className="mr-1" />
          Delete
        </button>
      )}
    </div>
  );
}

export function PurchaseOrdersPage() {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<ViewMode>('kanban');
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterVendor, setFilterVendor] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [vendors, setVendors] = useState<{ id: string; name: string }[]>([]);

  const [sortField, setSortField] = useState<string>('issueDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // Pagination
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;
  const totalPages = Math.ceil(total / limit);

  // @dnd-kit drag-and-drop state
  const [activeOrderId, setActiveOrderId] = useState<string | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: Record<string, string> = {};
      if (search) params.search = search;
      if (filterStatus) params.status = filterStatus;
      if (filterVendor) params.vendorId = filterVendor;
      if (sortField) params.sortBy = sortField;
      if (sortDir) params.sortDir = sortDir;
      params.page = String(page);
      params.limit = String(limit);
      const res = await api.get('/purchase-orders', { params });
      setOrders(res.data.data?.purchaseOrders ?? res.data.data ?? []);
      setTotal(res.data.pagination?.total ?? res.data.data?.total ?? res.data.total ?? res.data.data?.purchaseOrders?.length ?? 0);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load purchase orders');
    } finally {
      setLoading(false);
    }
  }, [search, filterStatus, filterVendor, sortField, sortDir, page]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Load vendor list once for the Vendor filter dropdown.
  useEffect(() => {
    api.get('/vendors', { params: { limit: 200 } })
      .then(res => setVendors(res.data.data?.vendors ?? res.data.data ?? []))
      .catch(() => setVendors([]));
  }, []);

  const ordersByStatus = (status: string) => orders.filter(o => o.status === status);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  const handleDndDragStart = (event: DragStartEvent) => {
    setActiveOrderId(event.active.id as string);
  };

  const handleDndDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveOrderId(null);
    if (!over) return;

    const orderId = active.id as string;
    const targetStatus = over.id as string;

    const order = orders.find(o => o.id === orderId);
    if (!order || order.status === targetStatus) return;

    setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: targetStatus } : o));

    try {
      await api.patch(`/purchase-orders/${orderId}/status`, { status: targetStatus });
    } catch (err: any) {
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: order.status } : o));
      alert(err.response?.data?.error || 'Failed to update status');
    }
  };

  const activeOrder = activeOrderId ? orders.find(o => o.id === activeOrderId) : null;

  const statusTotal = (status: string) => ordersByStatus(status).reduce((sum, o) => sum + o.total, 0);

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Purchase Orders</h1>
          <p className="text-sm text-gray-500">Track and manage procurement orders</p>
        </div>
        <div className="flex items-center gap-2">
          {/* View Toggle */}
          <div className="flex rounded-lg border border-gray-200 bg-white p-1">
            <button
              onClick={() => setViewMode('kanban')}
              className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${viewMode === 'kanban' ? 'bg-[var(--saffron-50)] text-[var(--ink-0)]' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <LayoutGrid size={16} />
              Kanban
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${viewMode === 'list' ? 'bg-[var(--saffron-50)] text-[var(--ink-0)]' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <List size={16} />
              List
            </button>
          </div>
          <button
            onClick={() => navigate('/purchase-orders/new')}
            className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] transition-colors"
          >
            <Plus size={16} />
            Create PO
          </button>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search purchase orders..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-4 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${showFilters ? 'border-[var(--ink-0)] bg-[var(--saffron-50)] text-[var(--ink-0)]' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}
        >
          <Filter size={16} />
          Filters
        </button>
      </div>

      {showFilters && (
        <div className="mb-4 flex flex-wrap gap-3 rounded-lg border border-gray-200 bg-white p-4">
          <select
            value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
          >
            <option value="">All Statuses</option>
            {COLUMNS.map(s => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
          <select
            value={filterVendor}
            onChange={(e) => { setFilterVendor(e.target.value); setPage(1); }}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
          >
            <option value="">All Vendors</option>
            {vendors.map(v => (
              <option key={v.id} value={v.id}>{v.name}</option>
            ))}
          </select>
          {(filterStatus || filterVendor) && (
            <button
              onClick={() => { setFilterStatus(''); setFilterVendor(''); setPage(1); }}
              className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
          <span className="ml-2 text-sm text-gray-500">Loading purchase orders...</span>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-20 text-red-500">
          <p className="text-sm">{error}</p>
          <button onClick={fetchOrders} className="mt-2 text-sm text-[var(--ink-0)] hover:underline">Retry</button>
        </div>
      ) : orders.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <ClipboardList size={40} className="mb-2" />
          <p className="text-sm">No purchase orders found</p>
          <p className="text-xs">Purchase orders will appear here once created</p>
        </div>
      ) : viewMode === 'kanban' ? (
        /* Kanban View */
        <DndContext sensors={sensors} onDragStart={handleDndDragStart} onDragEnd={handleDndDragEnd}>
          <div className="flex gap-4 overflow-x-auto pb-4">
            {COLUMNS.map(col => {
              const colOrders = ordersByStatus(col.key);
              return (
                <div
                  key={col.key}
                  className={`flex w-72 flex-shrink-0 flex-col rounded-xl border-t-2 ${col.color} border border-gray-200 bg-gray-50`}
                >
                  {/* Column Header */}
                  <div className="flex items-center justify-between bg-white px-4 py-3 rounded-t-xl border-b border-gray-200">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">{col.label}</h3>
                      <p className="text-xs text-gray-500">
                        {colOrders.length} orders &middot; {formatINR(statusTotal(col.key))}
                      </p>
                    </div>
                  </div>

                  {/* Order Cards */}
                  <PODroppableArea columnKey={col.key}>
                    {colOrders.length === 0 ? (
                      <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-200 py-8 text-gray-400">
                        <p className="text-xs">No orders</p>
                      </div>
                    ) : (
                      colOrders.map(order => (
                        <DraggablePOCard
                          key={order.id}
                          order={order}
                          isActive={activeOrderId === order.id}
                          onNavigate={() => navigate(`/purchase-orders/${order.id}/edit`)}
                          onDelete={() => {
                            if (window.confirm(`Delete purchase order ${order.number}?`)) {
                              api.delete(`/purchase-orders/${order.id}`).then(() => fetchOrders()).catch(() => alert('Failed to delete'));
                            }
                          }}
                        />
                      ))
                    )}
                  </PODroppableArea>
                </div>
              );
            })}
          </div>
          <DragOverlay dropAnimation={null}>
            {activeOrder ? (
              <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-lg w-72 opacity-90 rotate-2">
                <div className="mb-2 flex items-start justify-between">
                  <h4 className="text-sm font-medium text-gray-900">{activeOrder.formalNumber || activeOrder.number}</h4>
                  <GripVertical size={14} className="flex-shrink-0 text-gray-300" />
                </div>
                <p className="mb-2 text-xs text-gray-500">{activeOrder.vendor?.name || 'No vendor'}</p>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-900">{formatINR(activeOrder.total)}</span>
                  <span className="text-xs text-gray-400">{formatDate(activeOrder.issueDate)}</span>
                </div>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      ) : (
        /* List View */
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  <th className="cursor-pointer px-4 py-3" onClick={() => handleSort('number')}>
                    <span className="flex items-center gap-1">Number <SortIcon field="number" /></span>
                  </th>
                  <th className="px-4 py-3">Vendor</th>
                  <th className="cursor-pointer px-4 py-3" onClick={() => handleSort('status')}>
                    <span className="flex items-center gap-1">Status <SortIcon field="status" /></span>
                  </th>
                  <th className="cursor-pointer px-4 py-3" onClick={() => handleSort('issueDate')}>
                    <span className="flex items-center gap-1">Issue Date <SortIcon field="issueDate" /></span>
                  </th>
                  <th className="cursor-pointer px-4 py-3" onClick={() => handleSort('deliveryDate')}>
                    <span className="flex items-center gap-1">Delivery Date <SortIcon field="deliveryDate" /></span>
                  </th>
                  <th className="cursor-pointer px-4 py-3 text-right" onClick={() => handleSort('total')}>
                    <span className="flex items-center justify-end gap-1">Total <SortIcon field="total" /></span>
                  </th>
                  <th className="px-4 py-3 text-center w-16">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {orders.map(po => (
                  <tr key={po.id} onClick={() => navigate(`/purchase-orders/${po.id}/edit`)} className="hover:bg-gray-50 transition-colors cursor-pointer">
                    <td className="px-4 py-3 font-medium text-[var(--ink-0)]">{po.formalNumber || po.number}</td>
                    <td className="px-4 py-3 text-gray-600">{po.vendor?.name || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[po.status] || STATUS_COLORS.draft}`}>
                        {STATUS_LABELS[po.status] || po.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{formatDate(po.issueDate)}</td>
                    <td className="px-4 py-3 text-gray-600">{po.deliveryDate ? formatDate(po.deliveryDate) : '-'}</td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900">{formatINR(po.total)}</td>
                    <td className="px-4 py-3 text-center">
                      {po.status === 'draft' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(`Delete purchase order ${po.number}?`)) {
                              api.delete(`/purchase-orders/${po.id}`).then(() => fetchOrders()).catch(() => alert('Failed to delete'));
                            }
                          }}
                          className="inline-flex items-center justify-center rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={15} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700">
          <div className="text-sm text-gray-400">
            Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, total)} of {total}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="px-3 py-1 text-sm rounded border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed">Previous</button>
            <span className="text-sm text-gray-400">Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="px-3 py-1 text-sm rounded border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}